import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const generateEmojiSets = (count: number) => {
    const baseEmojis = ["🍎", "🚗", "🐶", "⚽", "🌞", "📕", "🐘", "🍔", "✈️", "🎈",
        "🚀", "🎸", "🦄", "🎃", "🥦", "📱", "🚴", "🍉", "🐳", "🏀",
        "🎺", "🌵", "⚔️", "🎠", "🍕", "🦜", "🏕", "🚗", "📖", "🎭",
        "🛶", "🎯", "⚡", "🐟", "📡", "💎", "🍩", "🦖", "🏎", "🦔",
        "🧀", "🔑", "🎹", "🌈", "🏖", "🦅", "🎭", "🌼", "🦎", "🛷",
        "🚦", "🐙", "🦆", "🚢", "📗", "🎻", "🌊", "🛸", "🏯", "🦊",
        "🚜", "📕", "🍯", "🧊", "🍇", "🏹", "🗻", "🍟", "🚂", "🐡",
        "🎢", "📌"];

    const sets = [];

    for (let i = 0; i < count; i++) {
        const commonEmoji = baseEmojis[Math.floor(Math.random() * baseEmojis.length)];
        let oddEmoji;

        do {
            oddEmoji = baseEmojis[Math.floor(Math.random() * baseEmojis.length)];
        } while (oddEmoji === commonEmoji);

        const set = new Array(4).fill(commonEmoji);
        const oddIndex = Math.floor(Math.random() * 4);
        set[oddIndex] = oddEmoji;

        sets.push(set);
    }

    return sets;
};

const OddOneOut: React.FC = () => {
    const [questionSet, setQuestionSet] = useState<string[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Odd One Out Worksheets for UKG | Logical Thinking Activities";
    }, []);

    function findOddOneOut(arr: string[]) {
        const countMap: { [key: string]: number } = {};

        // Count occurrences of each emoji
        arr.forEach(emoji => {
            countMap[emoji] = (countMap[emoji] || 0) + 1;
        });

        // Find the emoji that appears only once
        for (const emoji in countMap) {
            if (countMap[emoji] === 1) {
                return emoji;
            }
        }
        return ""; // If no odd one is found
    }

    const generateQuestion = () => {
        const randomSet = generateEmojiSets(1);
        setQuestionSet(randomSet[0]);
        setCorrectAnswer(findOddOneOut(randomSet[0]));
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (

        <>
            <Helmet>
                <meta name="description" content="Help UKG students build reasoning and categorization skills with Odd One Out activities. Download free worksheets that enhance observation and thinking." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/odd-one-out" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Odd One Out</h1>

                                    <p>
                                        The <strong>Odd One Out</strong> concept introduces UKG students to basic logic and reasoning. It helps children develop observation skills, pattern recognition, and the ability to group similar objects. This activity builds the foundation for early <strong>critical thinking</strong> in young learners.
                                    </p>

                                    <h2>🔍 What Is “Odd One Out”?</h2>
                                    <p>
                                        Children are presented with a set of pictures or objects, where one item doesn’t belong in the group. The child must identify which item is different and explain why. For example:
                                    </p>
                                    <ul>
                                        <li>Apple – Banana – Car – Grapes → <strong>Car</strong> is the odd one out because it’s not a fruit.</li>
                                        <li>Triangle – Square – Circle – Dog → <strong>Dog</strong> is the odd one out because it’s not a shape.</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Improve observation and categorization skills</li>
                                        <li>Develop early logic and reasoning</li>
                                        <li>Boost attention to detail and comparison</li>
                                        <li>Learn basic grouping and classification</li>
                                    </ul>

                                    <h2>📘 Worksheet Activities</h2>
                                    <ul>
                                        <li>Circle the object that is different in a group</li>
                                        <li>Match items that belong in the same category</li>
                                        <li>Color the odd object in each row</li>
                                        <li>Explain why an object is different using pictures</li>
                                        <li>Cut-and-paste exercises for group sorting</li>
                                    </ul>


                                    <h2>👩‍🏫 Tips for Teaching at Home or in Class</h2>
                                    <ul>
                                        <li>Use real objects like fruits, toys, or stationery to play live “Odd One Out” games</li>
                                        <li>Encourage children to explain their choices</li>
                                        <li>Involve children in sorting laundry, groceries, or blocks for practical grouping tasks</li>
                                        <li>Play memory or flashcard games with categories</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Make every day a logic game! Ask your child to spot the odd item at the dinner table, in their toy box, or in picture books. Let them explain their choice — it builds confidence and clarity.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Odd One Out</h1>

                                <p>
                                    The <strong>Odd One Out</strong> concept introduces UKG students to basic logic and reasoning. It helps children develop observation skills, pattern recognition, and the ability to group similar objects. This activity builds the foundation for early <strong>critical thinking</strong> in young learners.
                                </p>

                                <h2>🔍 What Is “Odd One Out”?</h2>
                                <p>
                                    Children are presented with a set of pictures or objects, where one item doesn’t belong in the group. The child must identify which item is different and explain why. For example:
                                </p>
                                <ul>
                                    <li>Apple – Banana – Car – Grapes → <strong>Car</strong> is the odd one out because it’s not a fruit.</li>
                                    <li>Triangle – Square – Circle – Dog → <strong>Dog</strong> is the odd one out because it’s not a shape.</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Improve observation and categorization skills</li>
                                    <li>Develop early logic and reasoning</li>
                                    <li>Boost attention to detail and comparison</li>
                                    <li>Learn basic grouping and classification</li>
                                </ul>

                                <h2>📘 Worksheet Activities</h2>
                                <ul>
                                    <li>Circle the object that is different in a group</li>
                                    <li>Match items that belong in the same category</li>
                                    <li>Color the odd object in each row</li>
                                    <li>Explain why an object is different using pictures</li>
                                    <li>Cut-and-paste exercises for group sorting</li>
                                </ul>


                                <h2>👩‍🏫 Tips for Teaching at Home or in Class</h2>
                                <ul>
                                    <li>Use real objects like fruits, toys, or stationery to play live “Odd One Out” games</li>
                                    <li>Encourage children to explain their choices</li>
                                    <li>Involve children in sorting laundry, groceries, or blocks for practical grouping tasks</li>
                                    <li>Play memory or flashcard games with categories</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Make every day a logic game! Ask your child to spot the odd item at the dinner table, in their toy box, or in picture books. Let them explain their choice — it builds confidence and clarity.
                                </p>
                            </article>
                            <h2>Find the Odd One Out</h2>
                            <div className="optionImageDiv" style={{ margin: "20px 0", display: "flex", justifyContent: "center", gap: "20px" }}>
                                {questionSet.map((object, index) => (
                                    <div key={index} style={{ cursor: "pointer" }} onClick={() => checkAnswer(object)}>
                                        {object}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default OddOneOut;
